import { useState } from 'react';
import { Mail, MapPin, Send, Check } from 'lucide-react';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-16 lg:pt-20">
      <div className="bg-[#111111] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="section-divider" />
          <h1 data-testid="contact-heading" className="font-bebas text-5xl lg:text-6xl text-white tracking-wider mb-2">
            CONTACT US
          </h1>
          <p className="font-inter text-sm text-zinc-500">Have a question? We're here to help.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Form */}
          <div>
            {submitted ? (
              <div data-testid="contact-success" className="bg-green-500/10 border border-green-500/30 p-10 text-center">
                <Check size={40} className="text-green-500 mx-auto mb-4" />
                <h2 className="font-bebas text-3xl text-white tracking-wider mb-2">MESSAGE SENT!</h2>
                <p className="font-inter text-sm text-zinc-400">We'll get back to you as soon as possible.</p>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-inter text-xs text-zinc-500 uppercase tracking-widest mb-2">Name</label>
                    <input
                      data-testid="contact-name"
                      type="text"
                      required
                      value={form.name}
                      onChange={e => setForm({...form, name: e.target.value})}
                      className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors placeholder-zinc-600"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block font-inter text-xs text-zinc-500 uppercase tracking-widest mb-2">Email</label>
                    <input
                      data-testid="contact-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={e => setForm({...form, email: e.target.value})}
                      className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors placeholder-zinc-600"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block font-inter text-xs text-zinc-500 uppercase tracking-widest mb-2">Subject</label>
                  <input
                    data-testid="contact-subject"
                    type="text"
                    value={form.subject}
                    onChange={e => setForm({...form, subject: e.target.value})}
                    className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors placeholder-zinc-600"
                    placeholder="Question about..."
                  />
                </div>
                <div>
                  <label className="block font-inter text-xs text-zinc-500 uppercase tracking-widest mb-2">Message</label>
                  <textarea
                    data-testid="contact-message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={e => setForm({...form, message: e.target.value})}
                    className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors placeholder-zinc-600 resize-none"
                    placeholder="Your message..."
                  />
                </div>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  className="flex items-center justify-center gap-2 w-full bg-orange-500 hover:bg-orange-600 text-white font-bebas text-lg tracking-widest py-4 transition-colors"
                >
                  <Send size={16} />SEND MESSAGE
                </button>
              </form>
            )}
          </div>

          {/* Info */}
          <div className="space-y-6">
            <div className="bg-[#111111] border border-white/10 p-6">
              <h3 className="font-bebas text-xl text-white tracking-wider mb-4">BEFORE YOU REACH OUT</h3>
              <p className="font-inter text-sm text-zinc-400 leading-relaxed mb-3">
                Many common questions are answered in our resources section. Check there first for the fastest answer.
              </p>
              <ul className="space-y-2">
                {[
                  { label: 'Muzzle Brake FAQ', href: '/resources/faq' },
                  { label: 'Measuring Guide', href: '/resources/measuring' },
                  { label: 'Installation Instructions', href: '/resources/installation' },
                ].map(l => (
                  <li key={l.href}>
                    <a href={l.href} className="font-inter text-sm text-orange-500 hover:text-orange-400 transition-colors">
                      {l.label} →
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#111111] border border-white/10 p-6">
              <h3 className="font-bebas text-xl text-white tracking-wider mb-4">OUR LOCATION</h3>
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-orange-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-inter text-sm text-white">Witt Machine & Tool Co.</p>
                  <p className="font-inter text-sm text-zinc-500">Texas, USA</p>
                  <p className="font-inter text-xs text-zinc-600 mt-2">SOT / Class III NFA Manufacturer</p>
                </div>
              </div>
            </div>

            <div className="bg-orange-500/10 border border-orange-500/30 p-6">
              <h3 className="font-bebas text-xl text-orange-400 tracking-wider mb-2">DEALER INQUIRIES</h3>
              <p className="font-inter text-sm text-zinc-400 leading-relaxed mb-4">
                Looking for wholesale pricing or dealer partnership? Visit our dedicated dealer program page.
              </p>
              <a href="/dealer-signup" className="font-bebas text-sm tracking-widest text-orange-500 hover:text-white transition-colors">
                APPLY FOR DEALER ACCOUNT →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
